import { useContext } from 'react'

import {
  Button, FileInput, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useInvoice, { useInvoiceForm } from '@hooks/useInvoice'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateAgencyPayableInvoiceModal'

const customRequiredFields = [
  { key: 'campaign_id' },
  { key: 'creditor_name' },
  { key: 'reference' },
  { key: 'total' },
  { key: 'due_date' },
]

type ModalCallbacks = {
  closeAllModals: () => void,
  createInvoice: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateAgencyPayableInvoiceModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeAllModals, createInvoice } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { invoice: initInvoice } = modalPayload

  const {
    callbacks: { createInvoice: createFn },
    creating,
    invoice,
  } = useInvoice(initInvoice)

  const {
    entityState,
    entityState: {
      creditor_name,
      due_date,
      paid,
      reference,
      total,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useInvoiceForm(invoice, { customRequiredFields, validateOn: 'change' })

  const createInvoicePayload = {
    callbacks: {
      action: createFn,
      afterAction: closeAllModals,
    },
    entityParams: entityState,
    requestOptions: {
      include_invoice_attachment_file: true,
      include_invoice_attachments: true,
      include_invoice_creditor: true,
      include_invoice_debtor: true,
      include_invoice_items: true,
    },
    toastText: 'Bill Created',
  }

  return (
    // @ts-expect-error TS2741
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      {/*
       // @ts-expect-error TS2559 */}
      <SidebarModal.Header callbacks={callbacks} title="New Payable" titleSecondLine="Invoice" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Creditor Name" errorMessage={errors.creditor_name}>
            <input name="creditor_name" type="text" value={creditor_name} {...handlers} />
          </Form.Field>

          <Form.Field label="* Reference" marginTop="large" errorMessage={errors.reference}>
            <input name="reference" type="text" value={reference} {...handlers} />
          </Form.Field>

          <Form.Row>
            <Form.Field label="* Due Date" marginTop="large" errorMessage={errors.due_date}>
              <input name="due_date" type="date" value={due_date} {...handlers} />
            </Form.Field>

            <Form.Field label="* Total" marginTop="large" errorMessage={errors.total}>
              <input name="total" type="number" value={total} {...handlers} />
            </Form.Field>
          </Form.Row>

          <Form.Field label="Paid" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              data-value-type="boolean"
              name="paid"
              value={paid}
              {...handlers}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          <Form.Field label="Upload Attachment" marginTop="large">
            <FileInput
              // @ts-expect-error TS2322
              name="receipt"
              input={{
                onChange: (fileData: object, fileObject: object) => {
                  // @ts-expect-error TS2339
                  setEntityState({ attachment: fileData, attachment_file_name: fileObject.name })
                },
              }}
            />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => createInvoice(createInvoicePayload)}
          size="large"
        >
          {saveEnabled ? 'Create Bill' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateAgencyPayableInvoiceModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
